import type { Listing } from '@thevillages/schemas';

import { isPlatformBrowser } from '@angular/common';
import {
  type AfterViewInit,
  Component,
  inject,
  input,
  output,
  PLATFORM_ID,
  signal,
  viewChild,
} from '@angular/core';

import { Environment } from '../../../environments/environment';
import { StateService } from '../../services/state/state';
import { Lightbox } from '../lightbox/lightbox';
import { ListingDetailsCta } from './cta/cta';
import { HomeListingDetails } from './home/home';
import { HomesiteListingDetails } from './homesite/homesite';
import { ListingDetailsSidebar } from './sidebar/sidebar';

@Component({
  imports: [
    HomeListingDetails,
    HomesiteListingDetails,
    Lightbox,
    ListingDetailsCta,
    ListingDetailsSidebar,
  ],
  selector: 'app-listing-details',
  templateUrl: './listing-details.html',
})
export class ListingDetails implements AfterViewInit {
  private readonly _platformId = inject(PLATFORM_ID);
  private readonly _stateService = inject(StateService);

  public readonly listing = input.required<Listing>();
  public readonly setRepFilter = output<string>();

  protected readonly homeListingDetails = viewChild(HomeListingDetails);
  protected readonly lightbox = viewChild(Lightbox);

  protected readonly bottomPadding = signal(Environment.safePaddingBottom);

  private readonly _isBrowser = signal(isPlatformBrowser(this._platformId));

  public ngAfterViewInit(): void {
    if (this._isBrowser()) {
      setTimeout(() => {
        document.getElementById('listing-details-wrapper')?.scrollIntoView();
      }, 50);
    }

    this.viewedAnalytics();
  }

  protected onLightboxClosed(lightboxIndex: number): void {
    this.homeListingDetails()?.selectedImage.set(lightboxIndex + 1);
  }

  protected openGallery(slideIndex: number): void {
    this.lightbox()?.show(slideIndex - 1, 'gallery');
  }

  protected openMap(): void {
    this.lightbox()?.show(0, 'map');
  }

  protected openTour(): void {
    this.lightbox()?.show(0, 'tour');
  }

  protected viewedAnalytics(): void {
    const _authorization = this._stateService.userToken()
      ? `Bearer ${this._stateService.userToken()}`
      : Environment.basicToken;

    const _options: RequestInit = {
      headers: {
        Authorization: _authorization,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- This is the correct header name for content type.
        'Content-Type': 'application/json',
      },
    };

    void fetch(`https://api.v2.thevillages.com/homes/listings/view/${this.listing().uliKey}`, {
      method: 'POST',
      ..._options,
    });

    void fetch('https://api.v2.thevillages.com/rest/homes/listing/view', {
      method: 'POST',
      ..._options,
      body: JSON.stringify({ listingId: this.listing().id }),
    });
  }
}
